import React, { useEffect, useState } from 'react';
import apiV2 from '../../utils/apiV2';
import { toast } from 'sonner';
import { Download, ChevronDown, FileText, Table, Lock } from 'lucide-react';
import { canExportData } from '../../utils/intelPerms';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { MonoNum } from './IntelligenceShell';

export const CARD = 'rounded-2xl bg-white/80 backdrop-blur-sm ring-1 ring-slate-900/[0.04] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_16px_40px_-24px_rgba(37,99,235,0.18)]';

export const BLUES = ['#2563EB', '#60A5FA', '#93C5FD', '#1E40AF', '#3B82F6', '#BFDBFE'];

export const useIntelligenceReport = (slug, months) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const orgId = localStorage.getItem('organizationId');
  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiV2.get(`/organizations/${orgId}/intelligence/${slug}?months=${months}`)
      .then((res) => alive && setData(res.data))
      .catch(() => alive && toast.error('Could not load this report'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [orgId, slug, months]);
  return { data, loading };
};

export const LiveBadge = () => (
  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
    <span className="relative flex h-1.5 w-1.5">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
    </span>
    Live
  </span>
);

export const KpiCard = ({ label, value, sub, testid, delay = 0 }) => (
  <div className={`${CARD} p-4 sm:p-5 intel-rise`} style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
    <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">{label}</div>
    <MonoNum className="text-lg sm:text-2xl font-extrabold tracking-tight leading-tight text-[#2563EB]">{value ?? '—'}</MonoNum>
    {sub && <div className="text-[11px] sm:text-xs text-slate-400 mt-1.5">{sub}</div>}
  </div>
);

export const ChartCard = ({ title, children, testid, delay = 0, className = '' }) => (
  <div className={`${CARD} p-4 sm:p-6 intel-rise ${className}`} style={{ animationDelay: `${delay}ms` }} data-testid={testid}>
    <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-4 sm:mb-5">{title}</div>
    {children}
  </div>
);

export const RangeControl = ({ months, setMonths, prefix }) => (
  <div className="flex rounded-full bg-slate-100/90 p-1">
    {[3, 6, 12].map((m) => (
      <button
        key={m}
        data-testid={`${prefix}-range-${m}m`}
        onClick={() => setMonths(m)}
        className={`px-3.5 sm:px-4 py-1.5 text-xs font-bold rounded-full transition-colors duration-150 ${months === m ? 'bg-white text-[#2563EB] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
      >
        {m}M
      </button>
    ))}
  </div>
);

export const ExportMenu = ({ onCsv, onPdf, testid }) => {
  if (!canExportData()) {
    return (
      <span
        data-testid={`${testid}-locked`}
        title="Your role doesn't allow exporting data — ask the account owner to enable it"
        className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-4 py-2 text-xs font-bold text-slate-400 cursor-not-allowed"
      >
        <Lock className="w-3.5 h-3.5" /> Export
      </span>
    );
  }
  return (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        data-testid={testid}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2 text-xs font-bold text-white hover:bg-[#1D4ED8] transition-colors duration-150 shadow-sm"
      >
        <Download className="w-3.5 h-3.5" /> Export <ChevronDown className="w-3.5 h-3.5" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="rounded-xl min-w-[160px]">
      {onCsv && (
        <DropdownMenuItem data-testid={`${testid}-csv`} onClick={onCsv} className="gap-2 font-semibold text-slate-700 cursor-pointer">
          <Table className="w-4 h-4 text-slate-400" /> Export CSV
        </DropdownMenuItem>
      )}
      <DropdownMenuItem data-testid={`${testid}-pdf`} onClick={onPdf} className="gap-2 font-semibold text-slate-700 cursor-pointer">
        <FileText className="w-4 h-4 text-slate-400" /> Export PDF
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
  );
};

export const ReportHeader = ({ title, blurb, right }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6 sm:mb-8 intel-rise">
    <div className="max-w-2xl">
      <div className="flex items-center gap-2.5 mb-3 flex-wrap">
        <LiveBadge />
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Your bookings only · updates continuously</span>
      </div>
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
      {blurb && <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{blurb}</p>}
    </div>
    <div className="flex items-center gap-2.5 flex-wrap">{right}</div>
  </div>
);

export const ReportSkeleton = () => (
  <div className="space-y-5">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
      {[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-2xl bg-slate-100/80 animate-pulse" />)}
    </div>
    <div className="h-64 rounded-2xl bg-slate-100/80 animate-pulse" />
  </div>
);

export const downloadCsv = (rows, filename) => {
  const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = filename;
  a.click();
};

export const exportPdf = ({ filename, title, subtitle, kpis = [], tables = [] }) => {
  const doc = new jsPDF();
  doc.setFontSize(16);
  doc.setTextColor(15, 23, 42);
  doc.text(title, 14, 18);
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(subtitle, 14, 24);
  doc.setDrawColor(37, 99, 235);
  doc.setLineWidth(0.8);
  doc.line(14, 28, 60, 28);
  let y = 34;
  if (kpis.length) {
    autoTable(doc, {
      startY: y,
      body: kpis,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 1.5 },
      columnStyles: { 0: { fontStyle: 'bold', textColor: [71, 85, 105], cellWidth: 55 } },
    });
    y = doc.lastAutoTable.finalY + 8;
  }
  tables.forEach((t) => {
    if (y > 260) { doc.addPage(); y = 20; }
    if (t.title) {
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      doc.text(t.title, 14, y);
      y += 3;
    }
    autoTable(doc, {
      startY: y,
      head: [t.head],
      body: t.rows,
      headStyles: { fillColor: [37, 99, 235], fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 2 },
      alternateRowStyles: { fillColor: [247, 249, 252] },
    });
    y = doc.lastAutoTable.finalY + 10;
  });
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text('HELM Intelligence by BookingFlows · your data only', 14, 290);
  }
  doc.save(filename);
};

export const tooltipStyle = { borderRadius: 12, border: 'none', boxShadow: '0 8px 24px -8px rgba(15,23,42,0.2)', fontSize: 12 };
